import { useContext, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  Link,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps,
  RadioToggle,
  RadioToggleGroup,
  Text,
} from '@mezzoforte/forge';
import { BiddingContext } from '@/context/BiddingContext';
import { useSession } from '@/hooks/useSession';
import { gtmService } from '@/util/gtm-service';
import { useHermesEntry } from '@/hooks/useHermesEntry';
import { isUsersBidWinning } from '@/util/bid';
import ReceiptTypeSelection from '@/features/Bidding/ReceiptTypeSelection';
import BidCostsBreakdown from '@/features/Bidding/BidCostsBreakdown';
import useSelection from '@/features/Bidding/useSelection';
import { DeliveryType, ReceiptChoice, ReceiptType } from '@/types/ListEntry';

export interface BidConfirmationModalProps {
  isOpen: ModalProps['isOpen'];
  onDismiss: ModalProps['onDismiss'];
  submitBid: () => void;
  entryId: number;
  entryTitle: string;
  bidValue: number;
  isAutobid?: boolean;
}

export default function BidConfirmationModal({
  isOpen,
  onDismiss,
  entryId,
  entryTitle,
  submitBid,
  bidValue,
  isAutobid,
}: BidConfirmationModalProps) {
  const confirmButton = useRef<HTMLButtonElement>(null);
  const context = useContext(BiddingContext);
  const session = useSession();
  const entryInfo = session.entryInfo(entryId);
  const isEmailConfirmed = session.currentUser.data?.user?.isEmailConfirmed;
  const selectionMutation = useSelection(entryId);
  const { hermesEntry } = useHermesEntry(entryId, context.vatPerc);
  const [selectedDeliveryType, setSelectedDeliveryType] = useState<DeliveryType | null>(
    entryInfo.data?.isLoggedIn ? entryInfo.data?.selectedDeliveryType : null
  );
  const [selectedReceiptType, setSelectedReceiptType] = useState<ReceiptType | null>(
    entryInfo.data?.isLoggedIn ? entryInfo.data?.selectedReceiptType : null
  );

  if (!entryInfo.data?.isLoggedIn) return null;

  const onClickConfirm = () => {
    submitBid();
    selectionMutation.mutate({
      deliveryType: selectedDeliveryType ?? context.deliveryMethods.at(0)?.type ?? DeliveryType.RETRIEVAL,
      receiptType: selectedReceiptType, // Null value allows user to add company information during payment
    });
    onDismiss();
  };

  const onClose = () => {
    gtmService.event('auction:close_bid_modal');
    onDismiss();
  };

  function receiptTypesForUser(): ReceiptChoice[] {
    if (!entryInfo.data?.isLoggedIn) return [];
    // If the user has already made a selection, the selection cannot be changed
    if (entryInfo.data.selectedReceiptType) return [];

    // The user must always choose if the bid is done as a company or not
    return [
      {
        type: ReceiptType.PRIVATE_PERSON,
        label: 'Yksityishenkilönä',
      },
      {
        type: ReceiptType.COMPANY,
        label: 'Yrityksenä',
      },
    ];
  }

  const canConfirmBid =
    (context.deliveryMethods.length <= 1 || selectedDeliveryType) &&
    (receiptTypesForUser().length <= 1 || selectedReceiptType);

  const hasMultipleDeliveryMethods = context.deliveryMethods.length > 1;
  const deliveryExpensesKnown = context.deliveryExpensesAvailable;

  const deliveryCannotBeSelected = hasMultipleDeliveryMethods && !deliveryExpensesKnown;
  const showDeliverySelection = hasMultipleDeliveryMethods && deliveryExpensesKnown;

  if (deliveryCannotBeSelected && !selectedDeliveryType) {
    setSelectedDeliveryType(DeliveryType.RETRIEVAL);
  }

  const hasHighestBid = isUsersBidWinning(hermesEntry?.bids ?? [], entryInfo.data.bidderId);

  return (
    <Modal
      headerDivider
      initialFocusRef={confirmButton}
      isOpen={isOpen}
      onDismiss={onClose}
      variant="custom"
      size="4xl"
      data-test="bid-confirmation-modal"
    >
      <ModalHeader data-test="bid-confirmation-modal-header">
        {isAutobid ? 'Aktivoidaanko korotusautomaatti maksimitarjouksella?' : 'Jätetäänkö tarjous?'}
      </ModalHeader>
      <ModalBody>
        <Text mb={3}>
          <strong>Kohde {entryId}</strong>: {entryTitle}
        </Text>
        <Grid
          templateColumns={{ md: '1fr 1fr' }}
          gap={3}
        >
          {(showDeliverySelection || receiptTypesForUser().length > 1) && (
            <Box>
              {/* Receipt type selection */}
              <ReceiptTypeSelection
                receiptTypes={receiptTypesForUser()}
                selectedReceiptType={selectedReceiptType}
                onChange={(t) => setSelectedReceiptType(t)}
              />
              {/* Delivery method selection */}
              {showDeliverySelection && (
                <FormControl>
                  <RadioToggleGroup
                    defaultValue={selectedDeliveryType?.toString()}
                    label="Toimitustapa"
                    helperText="Voit vielä vaihtaa toimitustapaa maksuvaiheessa. Toimituskulut koskevat toimitusta Manner-Suomeen."
                    data-test="delivery-select"
                  >
                    {context.deliveryMethods.map((deliveryMethod) => (
                      <RadioToggle
                        key={deliveryMethod.type}
                        value={deliveryMethod.type.toString()}
                        onChange={(e) => {
                          if (e.currentTarget.checked) setSelectedDeliveryType(deliveryMethod.type);
                        }}
                      >
                        {deliveryMethod.label}
                      </RadioToggle>
                    ))}
                  </RadioToggleGroup>
                </FormControl>
              )}
            </Box>
          )}
          {/* Total costs breakdown */}
          {(!hasMultipleDeliveryMethods || selectedDeliveryType) && (
            <BidCostsBreakdown
              bidValue={bidValue}
              selectedDeliveryType={selectedDeliveryType}
              selectedReceiptType={selectedReceiptType}
              isAutobid={isAutobid}
            />
          )}
        </Grid>
        {deliveryCannotBeSelected && (
          <Box mt={4}>
            <Text>Voit halutessasi pyytää ilmoittajalta toimitustarjouksen tälle kohteelle.</Text>
          </Box>
        )}
        <Box
          mt={4}
          data-test="terms-confirm"
        >
          <Text>
            {selectedReceiptType === ReceiptType.PRIVATE_PERSON && <span>Tarjoat yksityishenkilönä. </span>}
            {selectedReceiptType === ReceiptType.COMPANY && <span>Tarjoat yrityksenä. </span>}
            <span>Tarjoamalla hyväksyt </span>
            {context.isOwnPayment ? (
              <>
                <Link
                  target="_blank"
                  href="/kayttajien-valiset-huutokauppaehdot"
                >
                  käyttäjien välisten kauppojen huutokauppaehdot
                </Link>{' '}
                ja{' '}
                <Link
                  target="_blank"
                  href={`/kohde/${context.entryId}/${context.slug}/myyntiehdot`}
                >
                  kohteen myyntiehdot
                </Link>
              </>
            ) : (
              <>
                <Link
                  target="_blank"
                  href="/hk-huutokauppaehdot"
                >
                  huutokauppaehdot
                </Link>{' '}
                ja{' '}
                <Link
                  target="_blank"
                  href="/myyntiehdot"
                >
                  myyntiehdot
                </Link>
              </>
            )}
            .
          </Text>
        </Box>
        {hasHighestBid && (
          <Alert
            variant="danger"
            mt={3}
          >
            Sinulla on tällä hetkellä korkein tarjous kohteesta. Haluatko varmasti korottaa tarjoustasi? Voit myös
            aktivoida korotusautomaatin korottamatta nykyistä tarjousta.
          </Alert>
        )}
      </ModalBody>
      <ModalFooter data-test="bid-confirmation-modal-footer">
        <Box w="100%">
          <Button
            ref={confirmButton}
            variant="highlight"
            w="100%"
            onClick={onClickConfirm}
            disabled={!canConfirmBid || !isEmailConfirmed}
          >
            {isAutobid ? 'Aktivoi!' : 'Tarjoa!'}
          </Button>

          {!isEmailConfirmed && (
            <>
              <Text
                mt={3}
                variant="bold"
              >
                Vahvista vielä sähköpostiosoitteesi, jotta voit jättää tarjouksen.
              </Text>
              <Text mt={1}>
                Olemme lähettäneet sähköpostiisi vahvistuskoodin, joka tulee syöttää sivun ylälaidassa näkyvään
                laatikkoon. Tällä vahvistamme, että olet sähköpostiosoitteen haltija ja palvelun käytön kannalta
                olennaiset viestit tulevat sinulle perille.{' '}
              </Text>
            </>
          )}
        </Box>
      </ModalFooter>
    </Modal>
  );
}
